import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { QrCode, RefreshCw, Smartphone } from "lucide-react";

// Pengaturan → Nomor WhatsApp: kelola WA 2 & WA 3 (WAHA di server Tencent). Khusus owner:
// semua endpoint di sini butuh header X-Settings-Token dari /settings/unlock (dipasang Settings.js).
// WA 1 tetap diatur di blok "WhatsApp Gateway" di atas; di sini hanya nama menunya.
const inputCls = "w-full border border-[#E4E4E7] px-2 py-1.5 text-xs font-mono bg-white";

function LineCard({ cfg, onChange }) {
  const [form, setForm] = useState(cfg);
  const [status, setStatus] = useState("");
  const [qr, setQr] = useState("");
  const [busy, setBusy] = useState("");
  const pollRef = useRef(null);
  const line = cfg.line;
  const params = { line };
  const editable = line !== 1;

  useEffect(() => setForm(cfg), [cfg]);
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPoll(), []);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/wa/session/status", { params });
      setStatus(data.status || "");
      if (data.status === "WORKING") { setQr(""); stopPoll(); }
      return data.status || "";
    } catch (_) { return ""; }
  };
  useEffect(() => { loadStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/wa/lines/${line}`, { ...form, label: form.custom_label || "" });
      onChange(data);
      toast.success(`${data.label} disimpan`);
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    }
    setBusy("");
  };

  const fetchQr = async () => {
    const { data } = await api.get("/wa/qr", { params });
    setQr(`data:${data.mimetype || "image/png"};base64,${data.data}`);
  };

  const connect = async () => {
    setBusy("qr");
    try {
      const { data: st } = await api.post("/wa/session/start", null, { params });
      if ((st.status || "") === "WORKING") { toast.success("Sudah terhubung"); setQr(""); }
      else {
        await fetchQr();
        stopPoll();
        let ticks = 0;
        // QR diperbarui otomatis; berhenti saat terhubung atau ±2 menit
        pollRef.current = setInterval(async () => {
          ticks += 1;
          if (ticks > 12) { stopPoll(); return; }
          const s = await loadStatus();
          if (s === "WORKING") { toast.success("WhatsApp terhubung"); onChange(null); return; }
          try { await fetchQr(); } catch (_) { /* coba lagi */ }
        }, 10000);
      }
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengambil QR", { duration: 8000 });
    }
    setBusy("");
  };

  const reset = async () => {
    if (!window.confirm(`Reset sesi ${cfg.label}? Nomor ini akan logout dan perlu scan QR ulang.`)) return;
    setBusy("reset");
    try {
      await api.post("/wa/session/reset", null, { params });
      toast.success("Sesi direset — klik Scan QR");
      setQr("");
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal reset sesi", { duration: 8000 });
    }
    setBusy("");
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ok = status === "WORKING";
  return (
    <div className="border border-[#E4E4E7] bg-white p-3 space-y-2" data-testid={`wa-line-card-${line}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-bold text-sm flex items-center gap-2"><Smartphone size={14} className="text-[#16A34A]" /> WA {line}</div>
        <span className={`text-[10px] font-mono font-bold ${ok ? "text-[#16A34A]" : "text-[#DC2626]"}`} data-testid={`wa-line-status-${line}`}>
          {ok ? `TERHUBUNG${cfg.me ? ` · ${cfg.label}` : ""}` : status || "—"}
        </span>
      </div>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">Nama menu (kosong = otomatis pakai nomor)</span>
        <input value={form.custom_label || ""} onChange={(e) => set("custom_label", e.target.value)} placeholder={cfg.me ? cfg.label : `WA ${line}`} className={inputCls + " mt-1 font-sans"} />
      </label>
      {editable && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">URL WAHA</span>
              <input value={form.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="http://127.0.0.1:3021" className={inputCls + " mt-1"} />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">API Key WAHA</span>
              <input type="password" value={form.api_key || ""} onChange={(e) => set("api_key", e.target.value)} onFocus={(e) => { if (e.target.value === "***") set("api_key", ""); }} placeholder="kosongkan bila tidak diubah" className={inputCls + " mt-1"} />
            </label>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!form.enabled} onChange={(e) => set("enabled", e.target.checked)} className="accent-[#002FA7]" /> Aktifkan nomor ini
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={!!form.ai_enabled} onChange={(e) => set("ai_enabled", e.target.checked)} className="accent-[#002FA7]" /> Balas otomatis dengan AI Agent
          </label>
        </>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" onClick={save} disabled={!!busy} className="px-2 py-1 bg-[#111113] text-white text-[10px] font-bold uppercase disabled:opacity-50">{busy === "save" ? "Menyimpan…" : "Simpan"}</button>
        {editable && (
          <>
            <button type="button" onClick={loadStatus} disabled={!!busy} className="px-2 py-1 border border-[#E4E4E7] bg-white text-[10px] font-bold uppercase flex items-center gap-1 disabled:opacity-50"><RefreshCw size={10} /> Cek Status</button>
            <button type="button" onClick={connect} disabled={!!busy || !form.url} className="px-2 py-1 bg-[#002FA7] text-white text-[10px] font-bold uppercase flex items-center gap-1 disabled:opacity-50"><QrCode size={10} /> {busy === "qr" ? "Menyiapkan…" : "Mulai Session + QR"}</button>
            <button type="button" onClick={reset} disabled={!!busy || !form.url} className="px-2 py-1 bg-[#DC2626] text-white text-[10px] font-bold uppercase disabled:opacity-50">{busy === "reset" ? "Mereset…" : "Reset Session"}</button>
          </>
        )}
      </div>
      {qr && !ok && (
        <div className="space-y-1 pt-1">
          <img src={qr} alt={`QR WA ${line}`} className="w-52 h-52 border border-[#E4E4E7] bg-white" data-testid={`wa-line-qr-${line}`} />
          <div className="text-[10px] text-[#52525B]">Scan dari HP nomor ini: <b>Setelan → Perangkat Tertaut → Tautkan Perangkat</b>. QR diperbarui otomatis — biarkan halaman terbuka sampai TERHUBUNG.</div>
        </div>
      )}
      {!editable && <div className="text-[10px] text-[#A1A1AA]">URL, API key & QR WA 1 diatur di blok WhatsApp Gateway di atas.</div>}
    </div>
  );
}

export default function WaLinesSettings() {
  const [lines, setLines] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/wa/lines/admin");
      setLines(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat nomor WhatsApp");
      setLines([]);
    }
  };
  useEffect(() => { load(); }, []);
  const onChange = (d) => {
    if (!d) { load(); return; }
    setLines((p) => (p || []).map((x) => (x.line === d.line ? { ...x, ...d } : x)));
    window.dispatchEvent(new Event("wa-lines-changed"));
  };
  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="settings-wa-lines">
      <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
        <Smartphone size={14} />
        <div className="label-cap">Nomor WhatsApp — WA 1 · WA 2 · WA 3</div>
      </div>
      <div className="p-6 space-y-3">
        <p className="text-[11px] text-[#52525B]">
          WA 1 = nomor utama (sistem, bot & pelanggan). WA 2 & WA 3 = nomor pelanggan di server terpisah, dijawab AI Agent yang sama.
          Nama submenu otomatis memakai nomor yang terhubung. Semua balasan memakai jeda acak + "sedang mengetik…" (anti-restriksi Meta).
        </p>
        {lines === null ? (
          <div className="text-xs text-[#52525B]">Memuat…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {lines.map((l) => <LineCard key={l.line} cfg={l} onChange={onChange} />)}
          </div>
        )}
      </div>
    </div>
  );
}
